import React from "react";
import {
    Instagram, Mail, MapPin, MessageCircle, Facebook, Linkedin,
} from "lucide-react";
import { useSiteContent, getSection } from "../../context/SiteContentContext";

const FALLBACK = {
    tagline:
        "Naturalmente Premium. Sumos de polpa de fruta congelada — preparados na hora, em qualquer lugar.",
    instagram_url: "",
    facebook_url: "",
    linkedin_url: "",
    email: "info@saborliquido.pt",
    whatsapp_number: "",
    phone_display: "",
    address_display: "Portugal",
    nav_links: [
        { label: "Sabores", href: "#produtos" },
        { label: "Como funciona", href: "#como-funciona" },
        { label: "Onde Estamos", href: "/onde-estamos" },
        { label: "Parceiros", href: "#oportunidade" },
        { label: "Contacto", href: "#contacto" },
    ],
    copyright_suffix: "Naturalmente Premium",
};

const SocialIconLink = ({ href, ariaLabel, testid, hoverClass, Icon, disabled }) => {
    if (disabled) return null;
    return (
        <a
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
            aria-label={ariaLabel}
            data-testid={testid}
            className={`h-11 w-11 rounded-full bg-white/10 ${hoverClass} flex items-center justify-center transition-colors`}
        >
            <Icon className="h-5 w-5" />
        </a>
    );
};

export const Footer = () => {
    const { content } = useSiteContent();
    const f = { ...FALLBACK, ...getSection(content, "footer", {}) };
    const navLinks = Array.isArray(f.nav_links) && f.nav_links.length > 0
        ? f.nav_links
        : FALLBACK.nav_links;
    const waNumber = (f.whatsapp_number || "").replace(/\D/g, "");

    return (
        <footer className="bg-brand-ink text-white pt-20 pb-10 relative overflow-hidden">
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] bg-hex-pattern pointer-events-none"
                style={{ backgroundImage: "url('/brand/pattern-1.png')" }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-4 gap-10">
                    <div className="lg:col-span-2">
                        <img
                            src="/brand/logo-main.png"
                            alt="Sabor Líquido"
                            className="h-14 w-auto brightness-0 invert"
                        />
                        <p className="mt-5 text-white/75 max-w-md leading-relaxed">
                            {f.tagline}
                        </p>
                        <div className="mt-6 flex gap-3 flex-wrap">
                            <SocialIconLink
                                href={f.instagram_url}
                                ariaLabel="Instagram"
                                testid="footer-instagram"
                                hoverClass="hover:bg-brand-orange"
                                Icon={Instagram}
                                disabled={!f.instagram_url}
                            />
                            <SocialIconLink
                                href={f.facebook_url}
                                ariaLabel="Facebook"
                                testid="footer-facebook"
                                hoverClass="hover:bg-brand-orange"
                                Icon={Facebook}
                                disabled={!f.facebook_url}
                            />
                            <SocialIconLink
                                href={f.linkedin_url}
                                ariaLabel="LinkedIn"
                                testid="footer-linkedin"
                                hoverClass="hover:bg-brand-orange"
                                Icon={Linkedin}
                                disabled={!f.linkedin_url}
                            />
                            <SocialIconLink
                                href={f.email ? `mailto:${f.email}` : ""}
                                ariaLabel="Email"
                                testid="footer-email"
                                hoverClass="hover:bg-brand-orange"
                                Icon={Mail}
                                disabled={!f.email}
                            />
                            <SocialIconLink
                                href={waNumber ? `https://wa.me/${waNumber}` : ""}
                                ariaLabel="WhatsApp"
                                testid="footer-whatsapp"
                                hoverClass="hover:bg-brand-green"
                                Icon={MessageCircle}
                                disabled={!waNumber}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-orange font-bold">
                            Navegar
                        </div>
                        <ul className="mt-4 space-y-2 text-sm text-white/80">
                            {navLinks.map((l, i) => (
                                <li key={`${l.href}-${i}`}>
                                    <a
                                        href={l.href}
                                        className="hover:text-white"
                                        data-testid={`footer-nav-${i}`}
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-orange font-bold">
                            Contacto
                        </div>
                        <ul className="mt-4 space-y-3 text-sm text-white/80">
                            {f.address_display ? (
                                <li className="flex items-start gap-2" data-testid="footer-address">
                                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-brand-orange" />
                                    {f.address_display}
                                </li>
                            ) : null}
                            {f.email ? (
                                <li className="flex items-start gap-2" data-testid="footer-contact-email">
                                    <Mail className="h-4 w-4 mt-0.5 shrink-0 text-brand-orange" />
                                    {f.email}
                                </li>
                            ) : null}
                            {f.phone_display ? (
                                <li className="flex items-start gap-2" data-testid="footer-contact-phone">
                                    <MessageCircle className="h-4 w-4 mt-0.5 shrink-0 text-brand-orange" />
                                    {f.phone_display}
                                </li>
                            ) : null}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
                    <span>
                        © {new Date().getFullYear()} Sabor Líquido. Todos os direitos reservados.
                    </span>
                    {f.copyright_suffix ? <span>{f.copyright_suffix}</span> : null}
                </div>
            </div>
        </footer>
    );
};
